/**
 * ActivityEntry.jsx - one line of the Activity log (views/ActivityView.jsx),
 * with its two actions (Sharif, 10/8/26):
 *
 *   Jump    clicking the line opens the record it touched, through the app's
 *           own deep links. A record deleted since, or a change with no page
 *           of its own, says so on the line instead of opening nothing.
 *   Revert  the button at the end undoes it, through the same public API
 *           route the change used - so the area gate, validation, live
 *           updates and the audit log all apply, and the revert is a new
 *           entry. Always a confirm step first that lists exactly what will
 *           change; a field someone changed again since is never overwritten
 *           without asking ("Revert anyway").
 *
 * What each kind of entry does is decided in activityActions.js (pure, tested
 * in __tests__/activityActions.test.js); this file reads, asks and sends.
 */
import { useMemo, useState } from 'react';
import { jumpOf, resolveJump, revertOf, readOf, planUpdate, deletePlan, forced, conflictLines, mayRevert } from './activityActions';

const GREEN = '#1B5E20';
const BTN = {
  padding: '5px 12px',
  borderRadius: 6,
  fontSize: 10.5,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'Montserrat',
  border: `1px solid ${GREEN}`,
  background: '#fff',
  color: GREEN,
};
const BTN_MAIN = { ...BTN, background: GREEN, color: '#fff' };
const BTN_WARN = { ...BTN, background: '#B71C1C', border: '1px solid #B71C1C', color: '#fff' };

/** One fetch, its JSON (or {}) and status. Never throws. */
async function call(url, init) {
  try {
    const r = await fetch(url, init);
    const json = await r.json().catch(() => ({}));
    return { ok: r.ok, status: r.status, json };
  } catch {
    return { ok: false, status: 0, json: {} };
  }
}

function List({ title, items, color = '#333' }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ marginTop: 6 }}>
      {title && (
        <div style={{ fontSize: 9.5, fontWeight: 800, color: '#888', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>
          {title}
        </div>
      )}
      <ul style={{ margin: 0, paddingLeft: 16, color, fontSize: 11.5 }}>
        {items.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The line: time, who, what (clickable), the verb pill, and Revert.
 * `ctx` is what resolveJump checks a link against (the records the app holds
 * and canSee); `access` the person's effective access; `onReverted` re-reads
 * the log so the revert's own entry shows.
 */
export default function ActivityEntry({ row, time, initials, flavor, text, lines, created, ctx, access, onReverted, last }) {
  const jump = useMemo(() => jumpOf(row), [row]);
  const rv = useMemo(() => revertOf(row), [row]);
  const may = mayRevert(rv, access);
  const [note, setNote] = useState(''); // what a click on the line has to say
  const [step, setStep] = useState(null); // the revert in progress: { phase, plan, error, conflicts }

  const opens = !jump.reason;
  const open = async () => {
    const to = resolveJump(jump, ctx);
    if (to.text) {
      setNote(to.text);
      return;
    }
    if (to.verify) {
      setNote('Opening…');
      const r = await call(to.verify.url);
      if (r.status === 404 || (r.ok && !to.verify.pick(r.json))) {
        setNote(to.verify.missing);
        return;
      }
      setNote('');
    }
    window.location.hash = to.href;
  };

  const startRevert = async () => {
    if (!may.ok) {
      setNote(may.why);
      return;
    }
    setNote('');
    if (rv.kind === 'delete') {
      setStep({ phase: 'confirm', plan: deletePlan(row, rv) });
      return;
    }
    setStep({ phase: 'reading' });
    const read = readOf(row);
    let cur;
    if (read) {
      const r = await call(read.url);
      if (r.status === 404 || (r.ok && !read.pick(r.json))) {
        setStep({ phase: 'error', error: `This ${rv.noun} no longer exists - there is nothing to revert.` });
        return;
      }
      if (r.ok) cur = read.pick(r.json);
      // without the record's values now, only a route with the conflict guard may go on
      else if (!rv.expect) {
        setStep({ phase: 'error', error: `Could not read the ${rv.noun} as it is now, so nothing was changed. Try again.` });
        return;
      }
    }
    setStep({ phase: 'confirm', plan: planUpdate(row, cur) });
  };

  const send = async (request) => {
    setStep((s) => ({ ...s, phase: 'sending' }));
    const r = await call(request.url, {
      method: request.method,
      headers: request.body ? { 'Content-Type': 'application/json' } : undefined,
      body: request.body ? JSON.stringify(request.body) : undefined,
    });
    if (r.status === 409 && r.json.code === 'edit_conflict') {
      setStep((s) => ({ ...s, phase: 'conflict', conflicts: conflictLines(r.json) }));
      return;
    }
    if (!r.ok) {
      setStep((s) => ({
        ...s,
        phase: 'error',
        error:
          r.json.error || (r.status ? `The revert was refused (HTTP ${r.status}).` : 'Could not reach the server - nothing was changed.'),
      }));
      return;
    }
    setStep({ phase: 'done' });
    if (onReverted) onReverted();
  };

  const plan = step && step.plan;
  const busy = step && (step.phase === 'reading' || step.phase === 'sending');
  const label = rv.kind === null && row.method === 'DELETE' ? "Can't undo a delete" : 'Revert';
  const tip = !may.ok ? may.why : rv.kind === 'delete' ? 'Remove what this added (asks first)' : 'Put back what this changed (asks first)';

  return (
    <div
      title={`${row.method} ${row.path}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 16px',
        borderBottom: last ? 'none' : '1px solid #F4F6F1',
      }}
    >
      <span
        style={{
          fontSize: 10,
          color: '#999',
          fontWeight: 600,
          width: 62,
          flexShrink: 0,
          fontVariantNumeric: 'tabular-nums',
          paddingTop: 7,
        }}
      >
        {time}
      </span>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 7,
          background: GREEN,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 9.5,
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {initials}
      </div>
      <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#333', lineHeight: 1.45, wordBreak: 'break-word', paddingTop: 5 }}>
        <div
          role="link"
          tabIndex={0}
          aria-label={opens ? `Open: ${text}` : undefined}
          title={opens ? 'Open the record this changed' : jump.reason}
          onClick={open}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              open();
            }
          }}
          style={{ cursor: opens ? 'pointer' : 'default', outline: 'none' }}
        >
          <b style={{ color: '#0D0D0D' }}>{row.actor || row.actor_email}</b>{' '}
          {row.detail && row.detail.via === 'assistant' && (
            <span title="Confirmed from a GlobalOps Assistant proposal" style={{ fontSize: 10.5, color: '#557052', fontWeight: 700 }}>
              (via assistant){' '}
            </span>
          )}
          <span style={opens ? { textDecoration: 'underline', textDecorationColor: '#C8D5C4', textUnderlineOffset: 3 } : undefined}>
            {text}
          </span>
          {/* exactly what changed: one line per field, old -> new */}
          {lines.length > 0 && (
            <div style={{ marginTop: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {lines.map((ln) => (
                <div
                  key={ln.col}
                  style={{
                    fontSize: 11,
                    color: '#555',
                    background: '#F7F9F4',
                    border: '1px solid #E9EDE3',
                    borderRadius: 6,
                    padding: '3px 9px',
                    width: 'fit-content',
                    maxWidth: '100%',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {ln.text}
                </div>
              ))}
            </div>
          )}
          {/* what a created record IS */}
          {created && !text.includes(created) && (
            <div style={{ marginTop: 4, fontSize: 11, color: '#557052', fontWeight: 600 }}>{created}</div>
          )}
        </div>
        {note && (
          <div role="status" style={{ marginTop: 5, fontSize: 11, color: '#7A5A00' }}>
            {note}
          </div>
        )}

        {step && (
          <div
            role="region"
            aria-label="Revert"
            style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, border: '1px solid #DDE3D8', background: '#FBFCFA' }}
          >
            {step.phase === 'reading' && <div style={{ fontSize: 11.5, color: '#888' }}>Reading the record as it is now…</div>}
            {(step.phase === 'confirm' || step.phase === 'sending') &&
              plan &&
              (rv.kind === 'delete' ? (
                <>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#0D0D0D' }}>Revert: remove what this added?</div>
                  <List items={plan.lines} />
                  {plan.warn && <div style={{ marginTop: 6, fontSize: 11, color: '#B71C1C' }}>{plan.warn}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button type="button" disabled={busy} onClick={() => send(plan.request)} style={BTN_WARN}>
                      {busy ? 'Reverting…' : 'Delete it'}
                    </button>
                    <button type="button" disabled={busy} onClick={() => setStep(null)} style={BTN}>
                      Cancel
                    </button>
                  </div>
                </>
              ) : plan.nothing || !plan.request ? (
                <>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#0D0D0D' }}>
                    Nothing would change - every field already has its earlier value.
                  </div>
                  <List items={plan.already} color="#666" />
                  <List title="Not reverted" items={plan.skipped} color="#666" />
                  <div style={{ marginTop: 10 }}>
                    <button type="button" onClick={() => setStep(null)} style={BTN}>
                      Close
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#0D0D0D' }}>Revert this change? This will set:</div>
                  <List items={plan.lines} />
                  <List title="Changed again since this entry" items={plan.drifted} color="#92400E" />
                  <List title="Already as it was" items={plan.already} color="#666" />
                  <List title="Not reverted" items={plan.skipped} color="#666" />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button type="button" disabled={busy} onClick={() => send(plan.request)} style={plan.force ? BTN_WARN : BTN_MAIN}>
                      {busy ? 'Reverting…' : plan.force ? 'Revert anyway' : 'Revert'}
                    </button>
                    <button type="button" disabled={busy} onClick={() => setStep(null)} style={BTN}>
                      Cancel
                    </button>
                  </div>
                </>
              ))}
            {step.phase === 'conflict' && (
              <>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#92400E' }}>
                  Someone changed this record since - nothing was reverted yet.
                </div>
                <List items={step.conflicts} color="#92400E" />
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button type="button" onClick={() => send(forced(plan.request))} style={BTN_WARN}>
                    Revert anyway
                  </button>
                  <button type="button" onClick={() => setStep(null)} style={BTN}>
                    Cancel
                  </button>
                </div>
              </>
            )}
            {step.phase === 'done' && (
              <div role="status" style={{ fontSize: 11.5, fontWeight: 700, color: GREEN }}>
                Reverted. The revert is now the newest entry in the log.
              </div>
            )}
            {step.phase === 'error' && (
              <>
                <div role="alert" style={{ fontSize: 11.5, fontWeight: 600, color: '#B71C1C' }}>
                  {step.error}
                </div>
                <div style={{ marginTop: 8 }}>
                  <button type="button" onClick={() => setStep(null)} style={BTN}>
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <span
        style={{
          flexShrink: 0,
          marginTop: 5,
          fontSize: 8.5,
          fontWeight: 800,
          letterSpacing: 0.8,
          color: flavor.color,
          background: flavor.bg,
          border: `1px solid ${flavor.color}22`,
          padding: '2px 9px',
          borderRadius: 9,
        }}
      >
        {flavor.label}
      </span>
      <button
        type="button"
        aria-disabled={!may.ok || undefined}
        title={tip}
        onClick={startRevert}
        disabled={!!busy}
        style={{
          ...BTN,
          flexShrink: 0,
          marginTop: 1,
          padding: '3px 10px',
          fontSize: 10,
          ...(may.ok ? null : { borderColor: '#DDE3D8', color: '#AAB2A6', cursor: 'not-allowed' }),
        }}
      >
        {label}
      </button>
    </div>
  );
}
